import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import MarketingHeader from './home/MarketingHeader';
import { useContactLanding } from './home/useContactLanding';
import './home/HomeLanding.css';

const MACBOOK_SRC = '/home/macbook-smeny.png';
const WORKER_SRC = '/videos/404-video-final.mov';
/** Skip the last 10 hundredths of a second (0.10s) when looping */
const WORKER_TRIM_END_S = 0.07;

const NotFound: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const { user } = useAuth();
  const lang = language === 'en' ? 'en' : 'cs';
  const v = useContactLanding(lang);
  const workerRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = workerRef.current;
    if (!video) return;

    let raf = 0;
    const tick = () => {
      const end = video.duration;
      if (Number.isFinite(end) && end > WORKER_TRIM_END_S && video.currentTime >= end - WORKER_TRIM_END_S) {
        video.currentTime = 0;
        void video.play();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const homeHref =
    user?.role === 'SUPER_ADMIN' && !user?.impersonating
      ? '/admin'
      : user
        ? '/dashboard'
        : '/';

  return (
    <div
      className="home-landing relative min-h-screen overflow-hidden bg-[#F7F8FA]"
      style={{ width: '100%', fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <MarketingHeader
        v={v}
        lang={lang}
        onSetCz={() => setLanguage('cs')}
        onSetEn={() => setLanguage('en')}
      />

      {/* Full-bleed outline "404" behind content */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-hidden select-none"
      >
        <span
          className="font-bold leading-none tracking-tighter"
          style={{
            fontSize: 'clamp(180px, 42vw, 520px)',
            color: 'transparent',
            WebkitTextStroke: '2px #C5DBFD',
            paintOrder: 'stroke fill',
            opacity: 0.7,
            whiteSpace: 'nowrap',
          }}
        >
          404
        </span>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-12 pt-28 md:pt-32">
        <div className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
          {/* Visual: laptop + worker hammering into it */}
          <div className="relative w-full max-w-lg mx-auto md:mx-0 select-none overflow-visible" aria-hidden="true">
            <img
              src={MACBOOK_SRC}
              alt=""
              className="relative z-0 w-full h-auto drop-shadow-lg"
              draggable={false}
            />
            {/* Worker faces left — feet on MacBook bottom edge, hammering into the screen */}
            <video
              ref={workerRef}
              src={WORKER_SRC}
              className="pointer-events-none absolute z-10 w-[72%] max-w-none h-auto"
              style={{
                right: '-12%',
                bottom: '-62%',
                transform: 'rotate(-3deg)',
              }}
              autoPlay
              muted
              playsInline
              disablePictureInPicture
            />
          </div>

          {/* Copy + CTA */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <p className="type-label mb-3 tracking-wide uppercase">{t('notfound.code')}</p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 tracking-tight mb-3">
              {t('notfound.title')}
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-md mb-8">
              {t('notfound.description')}
            </p>
            <Link
              to={homeHref}
              className="home-btn-primary inline-flex items-center justify-center px-6 py-3 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition-colors shadow-sm"
              style={{ color: '#fff' }}
            >
              {t('notfound.cta')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
